import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import * as showdown from 'showdown';

import { AppinfoService, GeneralColType } from './appinfo.service';
import { CommonRequestService, SuccessResponse } from './common-request.service';
import { UserService } from './user.service';
import { AppSettings } from './../app.settings';
import { ModelMenuItemKey } from './main-data.service';


export interface LayerCategory {
	id: number;
	name: string;
	nameShort: string;
	geomType: 'POINT' | 'LINE' | 'AREA';
	code: string;
	scope: string;
	status: string;
	definedBy: string;
	parentTypeCode?: string;
	standardCols?: LayerCategoryStandardSchema[];
	hasCommon?: boolean; // Has common/all fields toggle
	tableName: string;
	isStateMaintained?: boolean;
	isCollaborative?: boolean;

		// The following properties is obtained from layerTypeList call. !!!! Relating to UsbId !!!!!
		isLoadableByCus?: boolean;
		
	colPrefix: string;

}
export interface LayerCategoryStandardSchema {
	cateName: string;
	id: number;
	colName: string;
	colType: string; // 'TEXT'
	generalColType: GeneralColType
	colLength: number;
	require: SchemaRequireType; // M: mandatory, C: conditional, O: optional
	desc: string; // markdown tooltip
	htmlTooltip: string; // html transformed from desc
	alias: string;
	definedBy: string;
	matchedCol?: any; // matching column in a layer
	prevMappingCol?: any; // previous loaded layer's mapping col
	defaultMatchName?: any; // default matching column
	isNguid: boolean; // is the column nguid column
	alterType: string; // the alternative col type
	isCommonField?: boolean;
	isFishboneCol?: boolean;
	valuePattern?: string; // regular expression // gss_value_pattern
	valueRange?: string; // e.g '[1, 10]' or '(1, 10]'...
	valueDomainNameNct?: string; // Nct domain
	models: ModelMenuItemKey[];
	seq: number;
	isUppercase: boolean;
	transferRclCol?: string;
	transferRclVal?: 'blank' | 'calc' | 'transfer' | 'parity';
	isCarryRclCol?: boolean;
}
// Schema Mapping
export interface SchemaMappingItem {
	id: number; // schema mapping id
	layerColumn: string;
	standardColumn: string;
	layerId: number;
	layerName: string;
	categoryId: number;
	categoryCode: string;
	customerId: number;
	prevLoadedLayerName: string;
	isFishboneCol?: boolean;
}
//
export interface UnmappedColumn {
	colLength: number;
	colType: string;
	colName: string;
	colId: number;
}

export type SchemaRequireType = 'M' | 'O' | 'C';

@Injectable()
export class FieldMappingService {

	// Store standard geom cols here since it's removed from getStandardSchema results
	standardGeomColsByCate: {
		[cateId: number]: LayerCategoryStandardSchema
	} = {};
	constructor(
		private toastr: ToastrService,
		private appinfoService: AppinfoService,
		private requestService: CommonRequestService,
		private userService: UserService
	) { }

	// Get layer category's standard schema (AZ and NENA)
	getStandardSchema(id) {
		return new Promise<LayerCategoryStandardSchema[]>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'azgiv/standardSchema';
			// Add state code
			const stateCode = this.userService.getSelectedCustomerInfo('stateCode');
			const params = {
				gltId: id.toString(),
				stateCode: stateCode.toString()
			};
			this.requestService.requestGet(url, {
				params: params
			}, (rsp) => {
				const schema = this.transformStandardSchema(rsp.data);
				// Remove geom col // geom col is only for database use
				const geomIdx = schema.findIndex(s => s.colType === 'GEOMETRY');
				if (geomIdx > -1) {
					this.standardGeomColsByCate[id] = schema[geomIdx];
					schema.splice(geomIdx, 1);
				}
				//
				resolve(schema);
				// Notice user no schema found
				if (!schema.length) {
					const currCategory = this.appinfoService.layerTypeList.find((d) => {
						return id === d.id;
					});
					this.toastr.error('No standard schema found for ' + currCategory.name);
				}
			}, err => reject(err));
		});
	}
	private transformStandardSchema(data: any[]): LayerCategoryStandardSchema[] {
		const transformedData: LayerCategoryStandardSchema[] = [];
		if (data) {
			const converter = new showdown.Converter();
			data.forEach((d) => {
				const colType = d.gss_col_type || '';
				// Get general column type
				let generalColType;
				const generalColTypeMap = this.appinfoService.lbDataTypesMap || {};
				for (const t of Object.keys(generalColTypeMap)) {
					if (generalColTypeMap[t].indexOf(colType.toLowerCase()) !== -1) {
						generalColType = t;
						break;
					}
				}
				transformedData.push({
					cateName: d.glt_name,
					colName: d.gss_col_name,
					alias: d.gss_alias,
					colType: colType,
					generalColType: generalColType,
					colLength: d.gss_col_length,
					id: d.gss_id,
					require: d.gss_status,
					desc: d.gss_desc,
					htmlTooltip: d.gss_desc ? converter.makeHtml(d.gss_desc) : '',
					definedBy: d.gss_defined_by,
					isNguid: d.nguid_col,
					alterType: d.alter_type,
					isCommonField: d.gss_common,
					isFishboneCol: d.gss_fishbone,
					valuePattern: d.gss_value_pattern,
					valueRange: d.gss_value_range,
					valueDomainNameNct: d.gss_nct_name,
					models: d.gss_data_models || [],
					seq: d.gss_seq,
					isUppercase: d.gss_upcase,
					transferRclCol: d.gss_transfer_rcl_col,
					transferRclVal: d.gss_transfer_rcl_val,
					isCarryRclCol: d.gss_carry_rcl_col
				});
			});
		} else {
			console.log('Standard Schema not found!');
		}
		return transformedData;
	}

	// Get schema mapping
	getSchemaMapping(layerCategoryId: number) {
		return new Promise<SchemaMappingItem[]>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'azgiv/schemaMapping';
			let customerId = this.userService.getCustomerIdByType();
			this.requestService.requestGet(url, {
				params: {
					customerId: customerId,
					gltId: layerCategoryId
				}
			}, (rsp: SuccessResponse<any[]>) => {
				const mapping: SchemaMappingItem[] = [];
				rsp.data = rsp.data || [];
				rsp.data.forEach((d) => {
					mapping.push({
						id: d.gsm_id,
						layerColumn: d.glc_name,
						standardColumn: d.gss_col_name,
						layerId: d.gly_id,
						layerName: d.gly_name,
						categoryId: d.glt_id,
						categoryCode: d.glt_cd,
						customerId: d.gly_cus_id,
						prevLoadedLayerName: d.gly_name,
						isFishboneCol: d.gss_fishbone
					});
				});
				resolve(mapping);
			}, err => reject(err));
		});
	}
	// Get previous mapped layer names of layer categories
	getPrevMapping(cusId?: number) {
		return new Promise<{prevLayerName;cateId;}[]>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'azgiv/schemaMapping';
			let customerId = cusId || this.userService.getCustomerIdByType();
			this.requestService.requestGet(url, {
				params: {
					customerId: customerId
				}
			}, (rsp: SuccessResponse<any[]>) => {
				rsp.data = rsp.data || [];
				const mapping = rsp.data.map((d) => {
					return {
						prevLayerName: d.gly_name,
						cateId: d.gly_glt_id
					}
				})
				resolve(mapping);
			}, err => reject(err));
		});
	}
	// Required either: 1.cusId + layerTypeId, or 2.layerId
	getUnmappedCols(cusId?: number, layerTypeId?, layerId?) {
		return new Promise<UnmappedColumn[]>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'azgiv/schemaUnmappedColumns';
			const params: any = {};
			if (layerTypeId) {
				params.customerId = cusId;
				params.layerTypeId = layerTypeId;
			} else if (layerId) {
				params.layerId = layerId;
			} else {
				reject({
					message: 'Either cusId + layerTypeId OR layerId is required'
				});
				return;
			}
			this.requestService.requestGet(url, {
				params: params
			}, (rsp: SuccessResponse<any[]>) => {
				const columns: UnmappedColumn[] = [];
				if (rsp.data) {
					rsp.data.forEach(d => {
						columns.push({
							colLength: d.glc_col_length,
							colId: d.glc_id,
							colName: d.glc_name,
							colType: d.glc_type,
							// layerCusId: 
						});
					});
				}
				resolve(columns);
			}, err => reject(err));	
		});
	}
}
